import { IconMinus, IconPlus } from '@tabler/icons-react';
import { Mod } from '@components/arsenal/mods/Mod';
import { useAppStore, UserData } from '@stores';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/ui/Button';
import { useUpgradeModMutation } from '@http';
import { Modal } from '@components/ui/Modal';
import * as Shared from '@shared/lib';
import { useState } from 'react';

interface ModUpgradeProps {
  mod: UserData['mods'][0];
  open: boolean;
  onClose: () => void;
};

export function ModUpgrade({ mod, open, onClose }: ModUpgradeProps) {
  const [level, setLevel] = useState(mod.level);

  const { t } = useTranslation(undefined, { keyPrefix: 'Mods' });
  const { mutateAsync } = useUpgradeModMutation();
  const { userData, isFetching, setIsFetching, refetch } = useAppStore();

  const resonite = userData.items
    .find(i => i.key === 'resonite')?.amount || 0;

  const incLevel = () => setLevel(level + 1);
  const decLevel = () => setLevel(level - 1);
  const maxLevel = 3 + mod.rank * 2;

  const currentResonite = Shared.ResoniteCalculator.calc(mod.level);
  const requiredResonite = currentResonite - Shared.ResoniteCalculator.calc(level);

  const upgrade = async () => {
    if (isFetching) return;
    setIsFetching(true);

    await mutateAsync({ id: mod.id, level });
    refetch();
    onClose();
  };

  return (
    <Modal title={t('upgradeModal')} onRequestClose={onClose} isOpen={open}>
      <div className="flex flex-col items-center p-4">
        <Mod previewLevel={level} mod={mod} />

        <div className="flex w-full items-center justify-between mt-4">
          <button
            className="mod-decrease-level"
            disabled={level <= mod.level}
            onClick={decLevel}
          >
            <IconMinus />
          </button>

          <p className="mod-level-text">
            {level >= maxLevel ? t('maxRank') : t('rank', { level: level + 1 })}
          </p>

          <button
            className="mod-increase-level"
            disabled={level >= maxLevel}
            onClick={incLevel}
          >
            <IconPlus />
          </button>
        </div>

        {level > mod.level && (
          <>
            <div className="border border-secondary flex mt-4 w-full">
              <div className="w-12 h-12 flex items-center justify-center p-2 border-r border-secondary">
                <img src="/assets/items/resonite.avif" alt="Resonite" />
              </div>
              <div className="flex flex-col justify-center px-2">
                <p className="leading-tight">{t('resonite')}</p>
                <p className={`${requiredResonite > resonite && 'text-red-500'} leading-tight`}>
                  {requiredResonite} / {resonite}
                </p>
              </div>
            </div>

            <Button disabled={requiredResonite > resonite} onClick={upgrade} className="mt-4">
              {t('upgrade')}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
}